import axios from 'axios';

const apiURL = process.env.REACT_APP_API_URL;

// A URL link attached to an enquiry, quote or job. These travel forward
// through conversions (enquiry -> quote -> job) — a link added to an enquiry
// is visible on the quote and job it becomes.
export interface EntityLink {
  entityLinkId: number;
  url: string;
  enquiryId: number | null;
  quoteId: number | null;
  jobId: number | null;
  createdDate: string | null;
  createdByName: string | null;
  modifiedDate: string | null;
  modifiedByName: string | null;
}

// Identifies which item the links belong to. Exactly one id is set, mirroring
// the addNoteDTO pattern used by the Notes tab.
export interface LinkSource {
  enquiryId: number | null;
  quoteId: number | null;
  jobId: number | null;
}

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('Authorization token not found');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + token,
  };
};

// Resolve the most specific entity segment + id for a source.
const resolveRoute = (source: LinkSource): { segment: string; id: number } | null => {
  if (source.jobId) return { segment: 'Job', id: source.jobId };
  if (source.quoteId) return { segment: 'Quote', id: source.quoteId };
  if (source.enquiryId) return { segment: 'Enquiry', id: source.enquiryId };
  return null;
};

export const getEntityLinks = async (source: LinkSource): Promise<EntityLink[]> => {
  const route = resolveRoute(source);
  if (!route) return [];

  const response = await axios.get(
    `https://${apiURL}/EntityLink/${route.segment}/${route.id}`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const addEntityLink = async (source: LinkSource, url: string): Promise<EntityLink> => {
  const route = resolveRoute(source);
  if (!route) {
    throw new Error('No entity selected to attach the link to.');
  }

  const response = await axios.post(
    `https://${apiURL}/EntityLink/${route.segment}/${route.id}`,
    { url },
    { headers: authHeaders() }
  );
  return response.data;
};

export const updateEntityLink = async (entityLinkId: number, url: string): Promise<EntityLink> => {
  const response = await axios.put(
    `https://${apiURL}/EntityLink/${entityLinkId}`,
    { url },
    { headers: authHeaders() }
  );
  return response.data;
};

export const deleteEntityLink = async (entityLinkId: number): Promise<void> => {
  await axios.delete(`https://${apiURL}/EntityLink/${entityLinkId}`, {
    headers: authHeaders(),
  });
};
